import { type ArticleListItem, type GetArticlesListData } from '@/shared/api/articles';
import { useArticlesApi } from '@/shared/api/useArticlesApi.ts';
import { useQuery } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, unref } from 'vue';

interface Params {
  city__slug?: string;
  region__slug?: string;
  country__slug?: string;
}

type ArticlesGridArticle = {
  created_at?: string;
  id?: number;
  intro?: string;
  lightbox_photo_url?: string;
  published_at?: string;
  reading_time?: number;
  slug?: string;
  tagline?: string;
  tags?: Array<number>;
  thumb_photo_url?: string;
  thumbnail_s?: string;
  thumbnail_m?: string;
  thumbnail_l?: string;
  thumbnail_xl?: string;
  title?: string;
  type?: string;
  updated_at?: string;
  url?: string;
};

const mapArticleToGridArticle = (article: ArticleListItem): ArticlesGridArticle => {
  return {
    created_at: article.created_at instanceof Date ? article.created_at.toISOString() : String(article.created_at),
    id: article.id,
    intro: article.intro,
    lightbox_photo_url: article.main_photo.lightbox,
    published_at:
      article.published_at instanceof Date ? article.published_at.toISOString() : String(article.published_at),
    reading_time: article.reading_time,
    slug: article.slug,
    tagline: article.tagline,
    tags: [],
    thumb_photo_url: article.main_photo.thumbnail,
    thumbnail_s: article.main_photo.thumbnail_s,
    thumbnail_m: article.main_photo.thumbnail_m,
    thumbnail_l: article.main_photo.thumbnail_l,
    thumbnail_xl: article.main_photo.thumbnail_xl,
    title: article.title,
    type: article.type,
    updated_at: article.updated_at instanceof Date ? article.updated_at.toISOString() : String(article.updated_at),
    url: article.url,
  };
};

const normalizeLocationQuery = (params: Params): GetArticlesListData['query'] => {
  const query: GetArticlesListData['query'] & { country_slug?: string } = {
    city_slug: params.city__slug,
    country_slug: params.country__slug,
    region_slug: params.region__slug,
    type: 'article' as const,
  };

  return query;
};

export const useArticlesBlockInfo = (locationSlug: MaybeRefOrGetter<Params>) => {
  const { getArticlesList } = useArticlesApi();

  const { data: articles, isFetched } = useQuery({
    queryKey: ['articlesBlockInfo', locationSlug],
    queryFn: () => {
      return getArticlesList({
        query: normalizeLocationQuery(unref(locationSlug) as Params),
      });
    },
    select: (response) => response.data?.results.map(mapArticleToGridArticle),
    retry: false,
  });

  return {
    isFetched,
    articles,
  };
};
